import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Form, Dropdown, Toast } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';
import { BsChevronDown } from 'react-icons/bs';
import { formatLocalDate, calculateShiftHours, findMatchingWeekEnding } from '../utils/shiftHelpers';

const formatShortDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
};

const formatFullDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatTime = (dateTimeString) => {
    const d = new Date(dateTimeString);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getWeekCommencing = (weekEndingStr) => {
    const d = new Date(weekEndingStr);
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
};

const RosterMobileTable = ({
    mobileData,
    roles,
    financialWeeks,
    createShiftVals,
    futureDateRestrict,
    selectedSite,
    onDuplicate,
    onDuplicateWeek,
    onRemove,
    onShiftCreate,
    pendingShifts,
    setPendingShifts,
    setRosterData,
    onRefresh,
    showToast,
    toast,
    onToastClose
}) => {
    const [showDupModal, setShowDupModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [checkedRows, setCheckedRows] = useState({});
    const [duplicateDate, setDuplicateDate] = useState(null);
    const [duplicateFinancialWeek, setDuplicateFinancialWeek] = useState({ description: 'Financial Week' });
    const [selectAll, setSelectAll] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [expandedWeeks, setExpandedWeeks] = useState({});

    const [weekForms, setWeekForms] = useState({});
    const shiftValuesRef = useRef({});

    const defaultRole = (roles?.[0] && typeof roles[0] === 'object') ? roles[0].Role : (roles?.[0] || 'DS');

    const getWeekForm = (weekKey) => weekForms[weekKey] || {
        date: '',
        startTime: '00:00',
        endTime: '00:00',
        role: defaultRole,
        qty: 1,
        staff: '',
        notes: ''
    };

    const updateWeekForm = (weekKey, field, value) => {
        setWeekForms(prev => ({
            ...prev,
            [weekKey]: {
                ...getWeekForm(weekKey),
                [field]: value
            }
        }));
    };

    const weekGroups = useMemo(() => {
        if (!mobileData || mobileData.length === 0) {
            const today = new Date();
            const dayOfWeek = today.getDay();
            const sunday = new Date(today);
            sunday.setDate(today.getDate() + (dayOfWeek === 0 ? 0 : 7 - dayOfWeek));
            const emptyGroups = [];
            for (let w = 0; w < 4; w++) {
                const we = new Date(sunday);
                we.setDate(sunday.getDate() + w * 7);
                emptyGroups.push({
                    weekEnding: we.toISOString(),
                    shifts: [],
                    totalShifts: 0,
                    totalHours: 0,
                    totalCost: 0,
                });
            }
            return emptyGroups;
        }

        const grouped = {};

        mobileData.forEach((shift, index) => {
            const key = shift.weekEnding || 'unknown';
            if (!grouped[key]) {
                grouped[key] = {
                    weekEnding: key,
                    shifts: [],
                };
            }
            grouped[key].shifts.push({ ...shift, _originalIndex: index });
        });

        return Object.values(grouped).map((group) => {
            const totalShifts = group.shifts.length;
            const totalHours = group.shifts.reduce((sum, s) => {
                if (s.shiftStart && s.shiftFinish) {
                    return sum + calculateShiftHours(s.shiftStart, s.shiftFinish);
                }
                return sum;
            }, 0);
            const totalCost = group.shifts.reduce((sum, s) => {
                const hours = (s.shiftStart && s.shiftFinish)
                    ? calculateShiftHours(s.shiftStart, s.shiftFinish)
                    : 0;
                return sum + (hours * (s.billRate || 0));
            }, 0);

            return {
                ...group,
                totalShifts,
                totalHours: Math.round(totalHours * 100) / 100,
                totalCost: Math.round(totalCost * 100) / 100,
            };
        });
    }, [mobileData]);

    useEffect(() => {
        if (weekGroups.length > 0) {
            setExpandedWeeks(prev => {
                const anyExpanded = Object.values(prev).some(Boolean);
                if (anyExpanded) return prev;
                return { [weekGroups[0].weekEnding]: true };
            });
        }
    }, [weekGroups]);

    const handleCloseRemoveModal = useCallback(() => setShowRemoveModal(false), []);
    const handleCloseDupModal = useCallback(() => setShowDupModal(false), []);

    const toggleWeek = useCallback((weekKey) => {
        setExpandedWeeks((prev) => ({
            ...prev,
            [weekKey]: !prev[weekKey],
        }));
    }, []);

    const handleCheckboxChange = useCallback((index) => {
        setCheckedRows((prevState) => {
            const updatedRows = { ...prevState };
            if (updatedRows[index]) {
                delete updatedRows[index];
            } else {
                updatedRows[index] = mobileData[index];
            }
            return updatedRows;
        });
    }, [mobileData]);

    const handleSelectAllInWeek = useCallback((weekGroup) => {
        setCheckedRows((prev) => {
            const updated = { ...prev };
            const allSelected = weekGroup.shifts.every(
                (s) => !!updated[s._originalIndex]
            );

            weekGroup.shifts.forEach((s) => {
                if (allSelected) {
                    delete updated[s._originalIndex];
                } else {
                    updated[s._originalIndex] = mobileData[s._originalIndex];
                }
            });

            return updated;
        });
    }, [mobileData]);

    const handleSelectAllToggle = useCallback(() => {
        if (selectAll) {
            setCheckedRows({});
        } else {
            const newCheckedRows = {};
            mobileData.forEach((_, index) => {
                newCheckedRows[index] = mobileData[index];
            });
            setCheckedRows(newCheckedRows);
        }
        setSelectAll(!selectAll);
    }, [selectAll, mobileData]);

    useEffect(() => {
        if (duplicateFinancialWeek.weekCommencing) {
            const formattedStartDate = formatLocalDate(duplicateFinancialWeek.weekCommencing);
            setDuplicateDate(formattedStartDate);
        }
    }, [duplicateFinancialWeek]);

    useEffect(() => {
        const shouldDisableButton = Object.values(checkedRows).some(
            (row) => row.editable === false
        );
        setIsButtonDisabled(shouldDisableButton);
    }, [checkedRows]);

    const handleDateChange = useCallback((e) => {
        setDuplicateDate(e.target.value);
        setDuplicateFinancialWeek({});
    }, []);

    const handleDuplicateConfirm = useCallback(async () => {
        await onDuplicate(duplicateDate, checkedRows);
        handleCloseDupModal();
        setCheckedRows({});
        setSelectAll(false);
    }, [onDuplicate, duplicateDate, checkedRows, handleCloseDupModal]);

    const handleRemoveConfirm = useCallback(async () => {
        await onRemove(checkedRows);
        handleCloseRemoveModal();
        setCheckedRows({});
        setSelectAll(false);
    }, [onRemove, checkedRows, handleCloseRemoveModal]);

    const handleDuplicateWeek = useCallback((e, weekGroup) => {
        e.stopPropagation();
        const weekChecked = {};
        weekGroup.shifts.forEach((s) => {
            weekChecked[s._originalIndex] = mobileData[s._originalIndex];
        });
        setCheckedRows(weekChecked);
        setShowDupModal(true);
    }, [mobileData]);

    const handleCreateShifts = useCallback((weekKey) => {
        const form = getWeekForm(weekKey);

        if (!form.date) {
            showToast?.('Error', 'Please select a date');
            return;
        }

        const [year, month, day] = form.date.split('-').map(Number);
        const [startH, startM] = form.startTime.split(':').map(Number);
        const [endH, endM] = form.endTime.split(':').map(Number);

        const shiftStartStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}:00`;
        const shiftFinishStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}:00`;

        const duration = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
        if (duration <= 0) {
            showToast?.('Error', 'Finish time must be after start time');
            return;
        }

        const shiftData = {
            shiftstart: shiftStartStr,
            shiftfinish: shiftFinishStr,
            rolename: form.role,
            qty: form.qty,
            notes: form.notes,
            venueid: createShiftVals.venueid.toString(),
            CODB: createShiftVals.codb,
            bun: createShiftVals.bun.toString()
        };

        const shiftDate = new Date(form.date);
        const dayOfWeek = shiftDate.getDay();
        const weekEnding = new Date(shiftDate);
        weekEnding.setDate(shiftDate.getDate() + (dayOfWeek === 0 ? 0 : 7 - dayOfWeek));

        const existingWeekEnding = findMatchingWeekEnding(weekEnding, mobileData);

        const pendingShiftsArr = [];
        const baseTimestamp = Date.now();

        for (let i = 0; i < form.qty; i++) {
            const pendingId = `pending-${baseTimestamp}-${i}-${Math.random().toString(36).substr(2, 9)}`;

            pendingShiftsArr.push({
                pendingId,
                shiftId: pendingId,
                shiftStart: shiftStartStr,
                shiftFinish: shiftFinishStr,
                roleName: form.role,
                staffName: form.staff || 'PENDING',
                notes: form.notes,
                weekEnding: existingWeekEnding || weekEnding.toISOString(),
                editable: true,
                billRate: 0,
                totalHours: duration,
                totalCost: 0,
                codb: createShiftVals.codb,
                site_name: selectedSite,
                isPending: true
            });
        }

        if (setPendingShifts) setPendingShifts(prev => [...prev, ...pendingShiftsArr]);
        if (setRosterData) setRosterData(prevShifts => [...prevShifts, ...pendingShiftsArr]);

        if (onShiftCreate) onShiftCreate(shiftData, pendingShiftsArr.map(p => p.pendingId));

        setWeekForms(prev => ({
            ...prev,
            [weekKey]: {
                date: '',
                startTime: '00:00',
                endTime: '00:00',
                role: defaultRole,
                qty: 1,
                staff: '',
                notes: ''
            }
        }));
    }, [weekForms, createShiftVals, mobileData, selectedSite, roles, setPendingShifts, setRosterData, onShiftCreate, showToast]);

    const hasCheckedRows = Object.keys(checkedRows).length > 0;

    const getWeekLabel = (weekEndingStr) => {
        if (!weekEndingStr || weekEndingStr === 'unknown') return 'Unknown Week';
        const commencing = getWeekCommencing(weekEndingStr);
        const ending = new Date(weekEndingStr);
        return `Week ${formatShortDate(commencing.toISOString())} to ${formatShortDate(ending.toISOString())}`;
    };

    const isWeekFullySelected = (weekGroup) =>
        weekGroup.shifts.length > 0 &&
        weekGroup.shifts.every((s) => !!checkedRows[s._originalIndex]);

    return (
        <>
            <Toast
                onClose={onToastClose}
                show={toast.visible}
                delay={5000}
                autohide
                bg="dark"
                className="mb-3"
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.title}</strong>
                </Toast.Header>
                <Toast.Body>{toast.message}</Toast.Body>
            </Toast>

            <Modal show={showDupModal} onHide={handleCloseDupModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Select start date of new pattern</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Control
                            type="date"
                            id="duplicate-shifts"
                            value={duplicateDate || ''}
                            onChange={handleDateChange}
                        />
                    </Form.Group>
                    <Dropdown className="ms-md-3">
                        <Dropdown.Toggle
                            className="w-100 mb-3 mb-md-0 text-dark"
                            variant="default"
                            id="dropdown-basic"
                        >
                            {duplicateFinancialWeek.description || 'Financial Week'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ maxHeight: 350 }}>
                            {financialWeeks.map((fwItem, index) => (
                                <Dropdown.Item
                                    key={index}
                                    onClick={() => { setDuplicateFinancialWeek(fwItem); }}
                                >
                                    {fwItem.description}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDupModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleDuplicateConfirm}>
                        Duplicate
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRemoveModal} onHide={handleCloseRemoveModal} className="text-center">
                <Modal.Header closeButton>
                    <Modal.Title>Are you sure you would like to delete selected shifts?</Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseRemoveModal}>
                        Cancel
                    </Button>
                    <Button variant="primary danger" onClick={handleRemoveConfirm}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            {hasCheckedRows && (
                <div className="w-100 justify-content-end mb-3">
                    {isButtonDisabled && (
                        <Toast bg="dark" className="me-3 mb-3">
                            <Toast.Body>
                                <FaLock /> Padlocked rows cannot be removed, only duplicated
                            </Toast.Body>
                        </Toast>
                    )}
                    <div className="d-flex flex-grow-1">
                        <Button
                            variant="primary me-3 flex-grow-1"
                            style={{ maxHeight: 45 }}
                            onClick={() => setShowDupModal(true)}
                        >
                            Duplicate Shifts
                        </Button>
                        <Button
                            disabled={isButtonDisabled}
                            style={{ maxHeight: 45 }}
                            variant="primary flex-grow-1"
                            onClick={() => setShowRemoveModal(true)}
                        >
                            Remove Shifts
                        </Button>
                    </div>
                </div>
            )}

            <div className="security-accordion">
                {weekGroups.map((group) => {
                    const isExpanded = !!expandedWeeks[group.weekEnding];
                    const weekLabel = getWeekLabel(group.weekEnding);
                    const form = getWeekForm(group.weekEnding);

                    return (
                        <div className="security-accordion__week" key={group.weekEnding}>
                            <div
                                className="security-accordion__header"
                                onClick={() => toggleWeek(group.weekEnding)}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                aria-label={`${weekLabel}, ${group.totalShifts} shifts`}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleWeek(group.weekEnding);
                                    }
                                }}
                            >
                                <div className="security-accordion__actions" style={{ gap: '10px', flex: 1 }}>
                                    <span className="security-accordion__title">
                                        {weekLabel}
                                    </span>
                                    {isExpanded && (
                                        <>
                                            <button
                                                className="security-accordion__dup-week"
                                                onClick={(e) => handleDuplicateWeek(e, group)}
                                                aria-label={`Duplicate week ${weekLabel}`}
                                            >
                                                Duplicate Week
                                            </button>
                                            <input
                                                type="checkbox"
                                                className="form-check-input security-accordion__week-checkbox"
                                                checked={isWeekFullySelected(group)}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleSelectAllInWeek(group);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                aria-label={`Select all shifts in ${weekLabel}`}
                                            />
                                        </>
                                    )}
                                </div>
                                <span
                                    className={
                                        `security-accordion__chevron${isExpanded ? ' security-accordion__chevron--expanded' : ''}`
                                    }
                                >
                                    <BsChevronDown />
                                </span>
                            </div>

                            {isExpanded && (
                                <div className="security-accordion__body">
                                    <div className="security-accordion__totals">
                                        {group.totalHours.toFixed(2)} Hours &times; {group.totalShifts} Shifts &times; &pound;{group.totalCost.toFixed(2)}
                                    </div>

                                    <div className="security-accordion__add-form">
                                        <div className="security-accordion__form-row">
                                            <input
                                                type="date"
                                                className="security-accordion__form-input security-accordion__form-input--date"
                                                placeholder="Date"
                                                value={form.date}
                                                onChange={(e) => updateWeekForm(group.weekEnding, 'date', e.target.value)}
                                            />
                                            <input
                                                type="time"
                                                className="security-accordion__form-input security-accordion__form-input--from"
                                                value={form.startTime}
                                                onChange={(e) => updateWeekForm(group.weekEnding, 'startTime', e.target.value)}
                                            />
                                            <input
                                                type="time"
                                                className="security-accordion__form-input security-accordion__form-input--to"
                                                value={form.endTime}
                                                onChange={(e) => updateWeekForm(group.weekEnding, 'endTime', e.target.value)}
                                            />
                                        </div>

                                        <div className="security-accordion__form-row">
                                            <select
                                                className="security-accordion__form-select security-accordion__form-select--role"
                                                value={form.role}
                                                onChange={(e) => updateWeekForm(group.weekEnding, 'role', e.target.value)}
                                            >
                                                {roles && roles.map((r, i) => (
                                                    <option key={i} value={typeof r === 'object' ? r.Role : r}>
                                                        {typeof r === 'object' ? r.Role : r}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                className="security-accordion__form-input security-accordion__form-input--qty"
                                                min="1"
                                                max="20"
                                                value={form.qty}
                                                onChange={(e) => updateWeekForm(group.weekEnding, 'qty', parseInt(e.target.value) || 1)}
                                            />
                                        </div>

                                        <input
                                            type="text"
                                            className="security-accordion__form-input security-accordion__form-input--full"
                                            placeholder="Staff name (optional)"
                                            value={form.staff}
                                            onChange={(e) => updateWeekForm(group.weekEnding, 'staff', e.target.value)}
                                        />

                                        <input
                                            type="text"
                                            className="security-accordion__form-input security-accordion__form-input--full"
                                            placeholder="Shift Notes"
                                            value={form.notes}
                                            onChange={(e) => updateWeekForm(group.weekEnding, 'notes', e.target.value)}
                                        />

                                        <div className="security-accordion__form-footer">
                                            <span className="security-accordion__form-hint">
                                                * Member of staff will be allocated by security provider
                                            </span>
                                            <button
                                                className="security-accordion__create-btn"
                                                onClick={() => handleCreateShifts(group.weekEnding)}
                                            >
                                                Create Shifts
                                            </button>
                                        </div>
                                    </div>

                                    <div className="security-accordion__divider" />

                                    {group.shifts.map((shift) => {
                                        const idx = shift._originalIndex;
                                        const isChecked = !!checkedRows[idx];

                                        return (
                                            <React.Fragment key={idx}>
                                                <div className="security-accordion__shift">
                                                    <div className="security-accordion__shift-header">
                                                        <div className="security-accordion__staff">
                                                            <div className="security-accordion__staff-left">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    style={{ cursor: 'pointer' }}
                                                                    id={`checkbox-${idx}`}
                                                                    checked={isChecked}
                                                                    onChange={() => handleCheckboxChange(idx)}
                                                                    aria-label={`Select shift for ${shift.staffName}`}
                                                                />
                                                                <span>{shift.staffName}</span>
                                                            </div>
                                                            {!shift.editable && (
                                                                <FaLock
                                                                    size={11}
                                                                    className="lock-icon-blue"
                                                                    aria-label="Locked shift"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="security-accordion__shift-data">
                                                        <div className="security-accordion__shift-col">
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Wk Ending: </span>
                                                                <span className="security-accordion__shift-value">
                                                                    {shift.weekEnding ? formatFullDate(shift.weekEnding) : '—'}
                                                                </span>
                                                            </div>
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Start: </span>
                                                                <span className="security-accordion__shift-value">
                                                                    {shift.shiftStart ? formatTime(shift.shiftStart) : '—'}
                                                                </span>
                                                            </div>
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Role: </span>
                                                                <span className="security-accordion__shift-value">{shift.roleName || '—'}</span>
                                                            </div>
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Notes: </span>
                                                                <span className="security-accordion__shift-value">{shift.notes || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="security-accordion__shift-col">
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Start Date: </span>
                                                                <span className="security-accordion__shift-value">
                                                                    {shift.shiftStart ? formatFullDate(shift.shiftStart) : '—'}
                                                                </span>
                                                            </div>
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-label">Finish: </span>
                                                                <span className="security-accordion__shift-value">
                                                                    {shift.shiftFinish ? formatFullDate(shift.shiftFinish) : '—'}
                                                                </span>
                                                            </div>
                                                            <div className="security-accordion__shift-field">
                                                                <span className="security-accordion__shift-value">Qty: {shift.qty || 1}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="security-accordion__divider" />
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

RosterMobileTable.propTypes = {
    mobileData: PropTypes.arrayOf(
        PropTypes.shape({
            staffName: PropTypes.string,
            roleName: PropTypes.string,
            billRate: PropTypes.number,
            weekEnding: PropTypes.string,
            shiftStart: PropTypes.string,
            shiftFinish: PropTypes.string,
            notes: PropTypes.string,
            editable: PropTypes.bool,
        })
    ).isRequired,
    roles: PropTypes.array,
    financialWeeks: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.string,
            weekCommencing: PropTypes.string,
        })
    ).isRequired,
    createShiftVals: PropTypes.shape({
        venueid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        codb: PropTypes.string,
        bun: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    futureDateRestrict: PropTypes.bool,
    selectedSite: PropTypes.string,
    onDuplicate: PropTypes.func.isRequired,
    onDuplicateWeek: PropTypes.func,
    onRemove: PropTypes.func.isRequired,
    onShiftCreate: PropTypes.func,
    pendingShifts: PropTypes.array,
    setPendingShifts: PropTypes.func,
    setRosterData: PropTypes.func,
    onRefresh: PropTypes.func,
    showToast: PropTypes.func,
    toast: PropTypes.shape({
        visible: PropTypes.bool,
        title: PropTypes.string,
        message: PropTypes.string,
    }).isRequired,
    onToastClose: PropTypes.func.isRequired,
};

export default RosterMobileTable;
